import React from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Phone, MapPin, ArrowRight, Languages } from "lucide-react";
import { Button } from "../components/ui/button";
import { IMAGES, CONTACT, TRYOUT } from "../mock";
import Seo from "../components/Seo";

const PROGRAMAS = [
  ["Niños pequeños (2.5–5 años)", "Lunes 4:15 PM · Miércoles 6:15 PM"],
  ["Little Warriors (4–9 años)", "Lunes 6:00 PM"],
  ["Niños (5–12 años)", "Lunes a jueves 5:00–6:00 PM · Sábado 10:30 AM"],
  ["Niños, adolescentes y adultos", "Martes y jueves 6:00–7:00 PM"],
  ["Adultos (todos los niveles)", "Lunes y miércoles 6:00 AM · Lunes a jueves 7:00 PM · Sábado 9:30 AM"],
];

const Espanol = () => (
  <div className="pt-24">
    <Seo
      title="Jiu-Jitsu Brasileño en Alameda — Primera clase GRATIS | Se habla español y portugués"
      description="Clases de Jiu-Jitsu Brasileño y defensa personal en Alameda, CA para niños, adolescentes y adultos. El Profesor Sergio Silva habla español y portugués. Tu primera clase es gratis."
      path="/es"
      breadcrumb={[{ name: "Home", path: "/" }, { name: "Español", path: "/es" }]}
    />
    <section className="relative overflow-hidden bg-[#0E1613] text-white">
      <img src={IMAGES.hero} alt="Jiu-Jitsu Brasileño en Alameda" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-16 sm:py-24">
        <span className="inline-flex items-center gap-2 rounded-full bg-br-yellow text-[#1A1A1A] px-4 py-1.5 text-xs font-bold tracking-wide" data-testid="es-badge">
          <Languages size={14} /> El Profesor Sergio Silva habla español y portugués
        </span>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
          Jiu-Jitsu Brasileño y Defensa Personal en Alameda
        </h1>
        <p className="mt-5 text-lg text-neutral-200 max-w-2xl">
          Clases para niños, adolescentes y adultos con el Profesor Sergio Silva, cinturón negro 5.º grado y campeón mundial. Sin experiencia necesaria. <strong className="text-white">Tu primera clase es GRATIS.</strong>
        </p>
        <Link to="/trial" data-testid="es-free-class-btn" className="inline-block mt-8 w-full sm:w-auto">
          <Button className="w-full sm:w-auto h-14 px-10 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base sm:text-lg font-extrabold">
            <CalendarCheck size={20} className="mr-2" /> RESERVA TU CLASE GRATIS
          </Button>
        </Link>
        <p className="mt-4 text-sm text-neutral-300">
          ¿Prefieres hablar con alguien? Llama o envía un mensaje al <a href={CONTACT.phoneHref} className="text-br-yellow font-semibold">{CONTACT.phone}</a> — en español, português o English. <Link to="/pt" className="underline">Versão em português</Link>
        </p>
      </div>
    </section>

    <section className="section-pad bg-[#F4F5F1]">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl border border-neutral-100 p-7">
          <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">¿Cómo funciona la clase gratis?</h2>
          <ul className="mt-4 space-y-3 text-neutral-700">
            {["Reserva en línea en un minuto (nombre, correo, teléfono y programa).", "Ven con pantalón deportivo y camiseta — nosotros ponemos el resto.", "Llega 10 minutos antes. Los padres pueden ver la clase.", "Sin compromiso ni pago. Después decides si quieres continuar."].map((t) => (
              <li key={t} className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-br-green shrink-0 mt-0.5" /> {t}</li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl bg-br-yellow/15 border border-br-yellow/40 p-4">
            <p className="font-semibold text-[#1A1A1A]">Oferta para nuevos estudiantes: {TRYOUT.price}</p>
            <p className="text-sm text-neutral-700 mt-1">Un mes de entrenamiento, 2 clases por semana y uniforme GRATIS. Pago único, sin renovación automática. Se puede pagar con Venmo, PayPal o Zelle.</p>
            <Link to="/tryout" className="inline-flex items-center gap-1 mt-2 text-sm font-bold text-br-green" data-testid="es-tryout-link">Ver la oferta de {TRYOUT.price} <ArrowRight size={14} /></Link>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-neutral-100 p-7">
          <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">Programas y horarios</h2>
          <ul className="mt-4 divide-y divide-neutral-100">
            {PROGRAMAS.map(([p, h]) => (
              <li key={p} className="py-3">
                <p className="font-semibold text-[#1A1A1A]">{p}</p>
                <p className="text-sm text-neutral-600">{h}</p>
              </li>
            ))}
          </ul>
          <div className="mt-5 space-y-2 text-sm text-neutral-700">
            <p className="flex items-center gap-2"><MapPin size={16} className="text-br-green" /> {CONTACT.addressShort} ({CONTACT.addressNote})</p>
            <p className="flex items-center gap-2"><Phone size={16} className="text-br-green" /> <a href={CONTACT.phoneHref} className="font-semibold text-br-green">{CONTACT.phone}</a> · {CONTACT.email}</p>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 mt-10 text-center">
        <Link to="/trial" data-testid="es-free-class-btn-2">
          <Button className="h-13 py-6 px-10 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base font-extrabold">
            <CalendarCheck size={18} className="mr-2" /> RESERVA TU CLASE GRATIS
          </Button>
        </Link>
        <p className="mt-3 text-xs text-neutral-500">El formulario está en inglés — si prefieres, llámanos y lo hacemos por ti en español.</p>
      </div>
    </section>
  </div>
);

export default Espanol;
